/**
 * CoverClubPage — "What makes a cover catch your eye?" A dedicated,
 * visual-first discovery space, deliberately not a 1-5 rating system.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fadeUp } from '@/lib/motion';
import AppHeader from '@/components/home/AppHeader';
import { bookHref } from '@/lib/bookModel';
import { useCoverClub } from './context/CoverClubContext';
import { CLUB_TABS } from './constants';
import CoverArt from './CoverArt';
import CoverFeed from './CoverFeed';
import BattlesSection from './BattlesSection';
import CollectionsSection from './CollectionsSection';
import { sortByTrending } from './utils/coverHelpers';
import { COVER_CLUB } from '@/constants/testIds';

const FAN_ROTATIONS = [-10, -3, 5, 12];

export default function CoverClubPage() {
  const { entries } = useCoverClub();
  const [tab, setTab] = useState('feed');
  const [activeCollection, setActiveCollection] = useState(null);
  const navigate = useNavigate();

  const handleOpenCover = (bookId) => navigate(bookHref(bookId));

  const heroCovers = useMemo(() => sortByTrending(entries).slice(0, 4), [entries]);

  return (
    <div className="min-h-screen overflow-hidden bg-cream" data-testid={COVER_CLUB.page}>
      <AppHeader />

      <div className="relative">
        <div aria-hidden className="pointer-events-none absolute -top-24 right-0 -z-0 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(240,199,127,0.32),transparent_65%)]" />

        <div className="u-container relative py-10 pb-28 md:py-14 md:pb-16">
          <div className="flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
            <motion.div variants={fadeUp} initial="hidden" animate="show" className="max-w-lg">
              <p className="u-eyebrow mb-3"><span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> What makes a cover catch your eye?</p>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">Cover Club</h1>
              <p className="mt-2 text-base text-subtle">
                React to covers, vote in cover battles, and discover your next read by what catches your eye first.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="hidden shrink-0 items-end pr-4 md:flex"
            >
              {heroCovers.map((entry, i) => (
                <motion.div
                  key={entry.bookId}
                  whileHover={{ y: -10, rotate: 0, zIndex: 10 }}
                  style={{ rotate: FAN_ROTATIONS[i], marginLeft: i === 0 ? 0 : -28 }}
                  className="h-32 w-[88px] shrink-0 overflow-hidden rounded-lg shadow-lift ring-4 ring-cream transition-transform"
                >
                  <CoverArt book={entry.book} style={entry.style} className="h-full w-full" />
                </motion.div>
              ))}
            </motion.div>
          </div>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setActiveCollection(null); }} className="mt-6">
            <TabsList className="h-auto rounded-full border border-line bg-card p-1">
              {CLUB_TABS.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  data-testid={`cover-club-tab-${t.key}`}
                  className="rounded-full px-4 py-1.5 text-xs font-semibold data-[state=active]:bg-coral-fill data-[state=active]:text-[#FFF7F0]"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="mt-6">
            {tab === 'feed' && <CoverFeed onOpenCover={handleOpenCover} />}
            {tab === 'battles' && <BattlesSection />}
            {tab === 'collections' && (
              <CollectionsSection activeCollection={activeCollection} onOpenCollection={setActiveCollection} onOpenCover={handleOpenCover} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
